import fs from 'node:fs';
import type { PreviewEnvironmentLogTails } from '../../manager/types.js';
import type { WirePreviewEnvironmentChildDeps } from './deps.js';
import { isStalePreviewEnvironmentChild } from './is-stale-child.js';

function logStopLine(
  d: WirePreviewEnvironmentChildDeps,
  code: number | null,
  signal: NodeJS.Signals | null,
): void {
  const { title } = d;
  if (signal) {
    d.log(`[preview-environment] Stopped ${title} (signal: ${String(signal)}).`);
    return;
  }
  if (code !== null && code !== 0) {
    const errTail = d.stderrTail.getTail().slice(-3).join('\n');
    d.log(`[preview-environment] Stopped ${title} (exit code ${code})${errTail ? `\n${errTail}` : ''}`);
    return;
  }
  d.log(`[preview-environment] Stopped ${title}.`);
}

function finishChildExit(
  d: WirePreviewEnvironmentChildDeps,
  code: number | null,
  signal: NodeJS.Signals | null,
  stale: boolean,
  cleanupDir?: string,
): void {
  if (cleanupDir) {
    d.rmMergedCleanupDir(cleanupDir);
  }
  if (stale) return;
  logStopLine(d, code, signal);
  d.detachProcessFromManager();
  const tails: PreviewEnvironmentLogTails = { stdout: d.stdoutTail.getTail(), stderr: d.stderrTail.getTail() };
  d.clearTailBuffers();
  const detail = signal
    ? `signal=${String(signal)}`
    : code != null && code !== 0
      ? `exit=${code}`
      : undefined;
  d.sendStatus(code === 0 || code == null ? 'stopped' : 'error', detail, tails);
}

export function attachChildExitHandler(d: WirePreviewEnvironmentChildDeps): void {
  const { proc, environmentId } = d;

  proc.on('exit', (code, signal) => {
    const stale = isStalePreviewEnvironmentChild(d);
    const poll = d.getPollInterval();
    if (poll && !stale) {
      clearInterval(poll);
      d.setPollInterval(undefined);
    }
    const cleanupDir = d.mergedCleanupDir;
    const mergedPath = d.mergedLogPath;

    if (mergedPath && !stale) {
      fs.readFile(mergedPath, (err, buf) => {
        if (!err && buf.length > d.mergedReadPos.value) {
          const chunk = Buffer.from(buf.subarray(d.mergedReadPos.value));
          if (chunk.length > 0) {
            d.stdoutTail.push(chunk);
            d.pushRemoteLogChunk(environmentId, 'stdout', chunk);
          }
        }
        finishChildExit(d, code, signal, isStalePreviewEnvironmentChild(d), cleanupDir);
      });
      return;
    }
    finishChildExit(d, code, signal, stale, cleanupDir);
  });
}
